import { useLiveQuery } from "dexie-react-hooks";
import { db, type Box, type Media } from "../db";
import styles from "./ArtBox.module.css";
import { distance } from "@turf/turf";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Check } from "lucide-react";

export function ArtBox({
  box,
  position,
  note,
  media,
}: {
  box: Box;
  position?: GeolocationPosition;
  note?: string;
  media?: Media[];
}) {
  return (
    <div key={box.id} className={styles.box}>
      {(media?.length || 0) > 0 ? (
        <UserImages media={media} />
      ) : (
        <BoxImages id={box.id} />
      )}

      <div>
        <h2 className={styles.title}>{box.title}</h2>
        <span>{box.artist}</span>
        <span>{box.year}</span>

        <span className={styles.distance}>
          {position && <Distance from={[box.lon, box.lat]} to={position} />}
        </span>
      </div>
      <div style={{ flex: 1 }}></div>
      {note && <p className={styles.note}>{note}</p>}
    </div>
  );
}

function BoxImages({ id }: { id: string }) {
  const images = useLiveQuery(() => {
    return db.boxImages.where({ artist: id }).toArray();
  }, [id]);

  return (
    <>
      {images?.slice(0, 1).map((da, i) => (
        <img
          key={i}
          src={da.thumb}
          height={100}
          width={80}
          style={{ objectFit: "cover" }}
        />
      ))}
    </>
  );
}

function UserImages({ media }: { media?: Media[] }) {
  //   const images = useLiveQuery(() => {
  //     return db.boxImages.where({ artist: id }).toArray();
  //   }, [id]);

  return (
    <>
      {media?.slice(0, 1).map((da, i) => (
        <BlobImage key={i} blob={da.file} />
      ))}
    </>
  );
}

function BlobImage({ blob }: { blob: Blob }) {
  const [src, setSrc] = useState<string>();

  useEffect(() => {
    const url = URL.createObjectURL(blob);

    setSrc(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  return (
    src && (
      <img src={src} height={100} width={80} style={{ objectFit: "cover" }} />
    )
  );
}

const ms = new Intl.NumberFormat(undefined, {
  style: "unit",
  unit: "meter",
  unitDisplay: "short",
  maximumFractionDigits: 0,
  maximumSignificantDigits: 2,
});

function Distance({
  from,
  to,
}: {
  from: [lon: number, lat: number];
  to: GeolocationPosition;
}) {
  const { latitude, longitude } = to.coords;

  const m = distance(from, [longitude, latitude], {
    units: "meters",
  });

  return <>&rarr; {ms.format(m)}</>;
}

//
export function ArtBoxCard({
  id,
  position,
}: {
  id: string;
  position?: GeolocationPosition | null;
}) {
  const box = useLiveQuery(() => db.boxes.get(id), [id]);

  const images = useLiveQuery(
    () => db.boxImages.where({ artist: id }).toArray(),
    [id]
  );

  const checkins = useLiveQuery(
    () => db.checkins.where({ boxId: id }).count(),
    [id]
  );

  const url = `/collect/${id}`;

  return (
    <div className={styles.card}>
      <header>
        <ol className={styles.images}>
          {images?.map((image, i) => (
            <li key={image.id}>
              <Link to={url}>
                <img
                  src={image.thumb}
                  fetchPriority={i === 0 ? "high" : "low"}
                  draggable={false}
                />
              </Link>
            </li>
          ))}
        </ol>
      </header>
      {box && (
        <main className={styles.cardContent}>
          <div>
            <h2 className={styles.title}>
              <Link to={url} className={styles.cardLink}>
                {box.title}
              </Link>
            </h2>
            <div>{box.artist}</div>
            <div>{box.year}</div>
          </div>

          <div style={{ textAlign: "right" }}>
            <div>{checkins && checkins > 0 ? <Check /> : null}</div>

            {position && <Distance from={[box.lon, box.lat]} to={position} />}
          </div>
        </main>
      )}
    </div>
  );
}
